import { Compass, Eye, Target } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { SEO } from "@/components/SEO";

const values = [
  { icon: Target, title: "Mission", text: "Permettre aux PME de tirer le meilleur parti des technologies numériques tout en garantissant une sécurité et une conformité optimales." },
  { icon: Eye, title: "Vision", text: "Devenir le partenaire de confiance des PME pour leur transformation numérique, avec des services de haute qualité favorisant croissance et compétitivité." },
  { icon: Compass, title: "Méthode", text: "Comprendre les usages, sécuriser le socle, automatiser les processus, puis mesurer les gains pour faire évoluer l’environnement en continu." },
];

export default function About() {
  return (
    <div className="px-5 pb-24 pt-36 sm:px-8 lg:pt-44" data-testid="about-page">
      <SEO title="À propos" description="Intellix SAS, cabinet de conseil en transformation numérique basé à Nantes. Expertise Microsoft 365, Copilot, Purview, cybersécurité Zero Trust et hébergement souverain pour les PME." path="/a-propos" />
      <div className="mx-auto max-w-7xl">
        <div className="grid items-center gap-14 lg:grid-cols-[0.95fr_1.05fr]">
          <Reveal>
            <SectionHeading
              eyebrow="À propos"
              title="Un partenaire de confiance pour les PME qui veulent avancer sans compromis."
              description="Intellix est spécialisée dans la transformation numérique des PME, avec des solutions intégrées et sécurisées pour optimiser la performance et la continuité des activités."
            />
          </Reveal>
          <Reveal delay={0.15}>
            <img
              src="https://images.pexels.com/photos/7108269/pexels-photo-7108269.jpeg?auto=compress&cs=tinysrgb&w=1400"
              alt="Professionnels en réunion autour d’un projet de transformation numérique"
              className="hero-image-mask h-[380px] w-full object-cover shadow-2xl lg:h-[480px]"
              data-testid="about-team-image"
            />
          </Reveal>
        </div>
        <div className="mt-20 grid gap-5 lg:grid-cols-3" data-testid="about-values-grid">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <Reveal key={value.title} delay={index * 0.08}>
                <article className="h-full rounded-3xl border border-border/70 bg-card/60 p-8" data-testid={`about-value-${value.title.toLowerCase()}`}>
                  <Icon className="h-8 w-8 text-primary" aria-hidden="true" />
                  <h2 className="mt-7 font-display text-2xl font-semibold tracking-tight">{value.title}</h2>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">{value.text}</p>
                </article>
              </Reveal>
            );
          })}
        </div>
        <Reveal className="mt-20">
          <div className="grid overflow-hidden rounded-[2rem] border border-border/70 bg-card/60 lg:grid-cols-2" data-testid="about-expertise-panel">
            <img
              src="https://images.pexels.com/photos/5324900/pexels-photo-5324900.jpeg?auto=compress&cs=tinysrgb&w=1400"
              alt="Équipe travaillant sur des solutions Cloud et Microsoft"
              className="h-full min-h-[320px] w-full object-cover"
              data-testid="about-office-image"
            />
            <div className="p-8 lg:p-12">
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary">Notre terrain d’expertise</p>
              <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight">Microsoft 365, Copilot, Purview, Cloud, IA, cybersécurité et équipements IT au service du quotidien.</h2>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                L’accompagnement Intellix privilégie des architectures sobres, sécurisées et adoptées par les équipes. Au-delà de l’écosystème Microsoft, nous proposons également des solutions d’hébergement sur des infrastructures françaises ou européennes pour répondre aux enjeux de souveraineté numérique et de conformité. Chaque recommandation vise un résultat opérationnel : gagner du temps, réduire les risques et améliorer la qualité de service.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
